import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  hasOtherReasonText,
  isOtherNotProceedingReason,
  notProceedingFieldVisibility,
  NOT_PROCEEDING_OTHER_MAX_LENGTH,
  reEngageDateNotInPast,
  requiredNotProceedingReason,
  requiredNotProceedingReasonOther,
} from '@gh/builder-shared';
import { Validators } from '@angular/forms';
import {
  DateTimeTypes,
  DictionaryItem,
  FormState,
  FormStateBuilder,
  getDialogInitData,
} from '@gh/common-shared';
import { DialogInput, FormMode } from '@gh/core-ui';

import { LeadBoardListItem } from '../../../../../resources/lead-board.model';

export type LostLeadReasonDialogConfig = {
  card: LeadBoardListItem;
};

export type LostLeadReasonDialogResult = {
  notProceedingReason: string;
  notProceedingReasonOther?: string;
  reEngageDate?: Date;
};

@Component({
  standalone: false,
  selector: 'gh-lost-lead-reason-dialog',
  templateUrl: 'lost-lead-reason-dialog.component.html',
  styleUrls: ['lost-lead-reason-dialog.component.scss'],
})
export class LostLeadReasonDialogComponent implements OnInit, DialogInput<LostLeadReasonDialogConfig> {
  input: LostLeadReasonDialogConfig;
  form: FormState<LostLeadReasonDialogResult>;
  reasons: DictionaryItem<any>[] = [];
  otherMaxLength = NOT_PROCEEDING_OTHER_MAX_LENGTH;

  constructor(private dialogRef: MatDialogRef<LostLeadReasonDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private formStateBuilder: FormStateBuilder) {
  }

  get otherLength(): number {
    return (this.form && this.form.value && this.form.value.notProceedingReasonOther || '').length;
  }

  ngOnInit(): void {
    const { notProceedingReasons } = getDialogInitData(this.dialogData);
    this.reasons = notProceedingReasons || [];
    this.form = this.formStateBuilder.buildPlain({
      mode: FormMode.EditMode,
      enabled: true,
      value: {
        notProceedingReason: '',
        notProceedingReasonOther: '',
        reEngageDate: undefined,
      },
      children: {
        notProceedingReason: { required: true, validators: [requiredNotProceedingReason] },
        notProceedingReasonOther: {
          required: false,
          visible: false,
          validators: [requiredNotProceedingReasonOther, Validators.maxLength(NOT_PROCEEDING_OTHER_MAX_LENGTH)],
        },
        reEngageDate: {
          type: DateTimeTypes.ShortDate, required: false, visible: false, validators: [reEngageDateNotInPast],
        },
      },
    });
  }

  onReasonChange(): void {
    const visibility = notProceedingFieldVisibility(undefined, this.form.value.notProceedingReason, true);
    const other = this.form.get('notProceedingReasonOther');
    const date = this.form.get('reEngageDate');
    other.visible = visibility.otherVisible;
    if (visibility.otherVisible) {
      other.enable();
    } else {
      other.setValue('');
      other.disable();
    }
    date.visible = visibility.reEngageVisible;
    if (visibility.reEngageVisible) {
      date.enable();
    } else {
      date.setValue(undefined);
      date.disable();
    }
  }

  isSaveDisabled(): boolean {
    if (!this.form || this.form.group.invalid) {
      return true;
    }
    const reason = this.form.value.notProceedingReason;
    if (!reason) {
      return true;
    }
    if (isOtherNotProceedingReason(reason)
      && !hasOtherReasonText(this.form.value.notProceedingReasonOther)) {
      return true;
    }
    return false;
  }

  onSave(): void {
    if (this.isSaveDisabled()) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close(this.form.submitValue);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
